import { Link, useNavigate } from 'react-router';
import { ArrowLeft } from 'lucide-react';

import { cn } from '@mds/shared/lib/utils';

export type PageHeaderProps = {
  /**
   * The way back up, as a destination — `{ label: 'Facilities', to: '/facilities' }`.
   *
   * A route rather than a history step, and that is the whole of the design
   * here. `navigate(-1)` is only the way back when the visitor arrived from
   * inside the site; from a shared link, a search result or a QR code on the
   * door it walks them out of the product entirely, and it does it silently —
   * the link looks identical either way. A route says where it goes and goes
   * there whatever the history holds.
   *
   * `history: true` is for a page reached from many places, where no one route
   * is the way back: the link then steps back one entry when the visitor came
   * from inside the site, and still goes to `to` when they did not.
   *
   * The label is the destination, not the word "Back". "Facilities" tells
   * someone where they will land; "Back" makes them guess, and on a page opened
   * from a link there is nothing to guess from.
   */
  back?: { label: string; to: string; history?: boolean };
  /**
   * The pill above the title — a category, a status. "Event hire".
   *
   * Set on the charcoal in the bright mint, the same treatment `MediaCard`
   * gives its own badge. Not the shared `Badge`: that one carries state in a
   * dense table — `success`, `danger` — and reaching for it here would put a
   * table's vocabulary on a page title.
   */
  badge?: string;
  /** The display line. The only part that is not optional. */
  title: string;
  /** What this page is, in one breath. */
  description?: string;
  /**
   * The mono facts under it — "From $120", "Fixed block", "2 PM – 10 PM".
   *
   * Separate strings rather than one pre-joined line, so they wrap onto their
   * own rows on a narrow screen instead of breaking mid-fact. Uppercased here,
   * so a caller writes them in sentence case and the component sets them.
   */
  meta?: string[];
  className?: string;
};

/**
 * The opener for an ordinary page: a pill, a display line, a sentence and a row
 * of facts, left-aligned on the page's own ground.
 *
 * Not `Hero`, and the difference is the ground rather than the shape. A hero is
 * full-bleed over a photograph, so every colour in it is measured against a
 * scrim and it pulls itself up under the header. This sits on the page like any
 * other section, which is why it can use the ordinary ink roles and why it has
 * no media, no scrim and no call to action.
 *
 * `h1` on purpose: this is the page's heading, and a page gets one. Dropping
 * two of these onto the same screen is a bug in the page, not in the component.
 */
export default function PageHeader({
  back,
  badge,
  title,
  description,
  meta,
  className,
}: PageHeaderProps) {
  const navigate = useNavigate();

  return (
    <header className={cn('flex flex-col items-start', className)}>
      {back && (
        <Link
          to={back.to}
          onClick={(event) => {
            // The router numbers its history entries in `idx`, and a `replace`
            // keeps the number, so above 0 there is a page of ours behind this
            // one. At 0 the visitor landed here, and the plain link applies.
            const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
            if (back.history && idx > 0) {
              event.preventDefault();
              navigate(-1);
            }
          }}
          /* The visible text is the destination, so the accessible name says
             what the arrow says silently. It contains the visible text, which
             is what keeps a voice-control user able to say what they see. */
          aria-label={`Back to ${back.label}`}
          className={cn(
            'group inline-flex items-center gap-2 rounded-sm',
            'font-mono text-2xs text-brand-text uppercase',
            /* No underline on hover: the arrow sliding back is the response.
               There is no darker mint to move to either, since `--brand-text`
               and `--brand-strong` are both mint 800. */
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
          )}
        >
          <ArrowLeft
            aria-hidden
            className={cn(
              'size-3.5 transition-transform duration-(--duration-fast) ease-standard',
              'group-hover:-translate-x-0.5 motion-reduce:transition-none',
            )}
          />
          {back.label}
        </Link>
      )}

      {badge && (
        <span
          className={cn(
            'rounded-button bg-panel px-3.5 py-1.5',
            'font-mono text-2xs font-semibold text-brand',
            back && 'mt-5',
          )}
        >
          {badge}
        </span>
      )}

      {/* `text-balance` so a two-line title breaks into two even lines rather
          than a long one and an orphan. It only applies up to a few lines,
          which is the right limit — a display line is never more than that. */}
      <h1 className={cn('text-heading-xl text-balance', (badge || back) && 'mt-6')}>
        {title}
      </h1>

      {description && (
        <p className={cn('max-w-prose text-base text-pretty text-ink-muted', 'mt-5')}>
          {description}
        </p>
      )}

      {meta && meta.length > 0 && (
        // Wider gaps than the same row inside a card: there the facts are
        // crowded into a column a few hundred pixels across, here they have the
        // page and would read as one run-on line at `gap-x-4`.
        <div
          className={cn(
            'mt-8 flex flex-wrap gap-x-8 gap-y-2',
            'font-mono text-2xs text-brand-text uppercase',
          )}
        >
          {meta.map((fact) => (
            <span key={fact}>{fact}</span>
          ))}
        </div>
      )}
    </header>
  );
}
